import { Component } from '@theme/component';

/**
 * Painel "Departamentos" do header desktop (TASK-017, hover/teclado na TASK-019).
 *
 * Progressive enhancement puro: sem JS o gatilho continua sendo um <a> real
 * para /collections/ e os quick links sao ancoras normais. Com JS ele vira um
 * disclosure (padrao APG disclosure navigation — nada de role="menu"), e so
 * entao role/aria-controls/aria-expanded passam a existir no DOM.
 *
 * Orcamento (performance-web): zero listener de scroll/resize/pointermove; os
 * de `document` (click fora, focusin fora, Escape) so existem enquanto aberto
 * e caem com o AbortController; flyout das filhas e 100% CSS.
 */
class ShopinfoDepartmentsComponent extends Component {
  /** @type {AbortController | null} */
  #lifetime = null;

  /** @type {AbortController | null} */
  #whileOpen = null;

  /** @type {number | undefined} */
  #openTimer;

  /** @type {number | undefined} */
  #closeTimer;

  /** @returns {HTMLAnchorElement | null} */
  get #trigger() {
    return this.querySelector('[data-departments-trigger]');
  }

  /** @returns {HTMLElement | null} */
  get #panel() {
    return this.querySelector('[data-departments-panel]');
  }

  get #isOpen() {
    return this.dataset.open === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    const trigger = this.#trigger;
    const panel = this.#panel;
    if (!trigger || !panel) return;

    // ARIA e role so passam a existir quando ha JS para honra-los.
    trigger.setAttribute('role', 'button');
    if (panel.id) trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-expanded', 'false');

    this.#lifetime = new AbortController();
    const { signal } = this.#lifetime;

    trigger.addEventListener('click', this.#onTriggerClick, { signal });
    trigger.addEventListener('keydown', this.#onTriggerKeydown, { signal });
    this.addEventListener('focusin', this.#syncExpandedParents, { signal });

    // Hover e camada SOBRE o clique, so sob ponteiro fino com hover real — em
    // touch nem existe, o que mata abertura fantasma na origem. O wrap contem
    // gatilho E painel: ir de um ao outro nao dispara pointerleave, e o painel
    // persiste sob o ponteiro (WCAG 1.4.13). Os 150ms filtram travessia rapida
    // do header; os 300ms toleram a saida, e reentrar cancela.
    const wrap = trigger.parentElement;
    if (wrap && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      wrap.addEventListener('pointerenter', this.#onPointerEnter, { signal });
      wrap.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimers();
    this.#lifetime?.abort();
    this.#whileOpen?.abort();
    this.#lifetime = null;
    this.#whileOpen = null;
  }

  #clearTimers() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
  }

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    event.preventDefault();
    this.#toggle();
  };

  /**
   * role="button" implica Enter E Space. Enter ja vira click no <a>; Space
   * precisa disto, com preventDefault contra o scroll.
   *
   * @param {KeyboardEvent} event
   */
  #onTriggerKeydown = (event) => {
    if (event.key !== ' ') return;
    event.preventDefault();
    this.#toggle();
  };

  /**
   * Esc fecha com o foco em qualquer lugar (WCAG 1.4.13).
   *
   * @param {KeyboardEvent} event
   */
  #onEscape = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.#close();
  };

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#closeTimer);
    this.#openTimer = setTimeout(() => this.#open(), 150);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#openTimer);
    this.#closeTimer = setTimeout(() => this.#close(), 300);
  };

  /** @param {Event} event */
  #onOutsideInteraction = (event) => {
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;
    this.#close();
  };

  /**
   * Reflete no aria-expanded dos pais qual flyout esta aberto pelo teclado.
   * O hover e puramente visual e nao mexe em ARIA de proposito.
   *
   * @param {Event} event
   */
  #syncExpandedParents = (event) => {
    const target = event.target;
    const parents = this.querySelectorAll('[data-departments-parent]');

    for (const parent of parents) {
      const item = parent.closest('[data-departments-item]');
      const expanded = Boolean(item && target instanceof Node && item.contains(target));
      parent.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  };

  // Aberto por hover, o clique fecha — toggle universal, sem reabertura: o
  // ponteiro ja esta no wrap e pointerenter so dispara de novo na reentrada.
  #toggle() {
    if (this.#isOpen) {
      this.#close({ restoreFocus: false });
    } else {
      this.#open();
    }
  }

  #open() {
    this.#clearTimers();
    if (this.#isOpen) return;

    this.dataset.open = 'true';
    this.#trigger?.setAttribute('aria-expanded', 'true');

    this.#whileOpen = new AbortController();
    const { signal } = this.#whileOpen;

    document.addEventListener('click', this.#onOutsideInteraction, { signal });
    document.addEventListener('focusin', this.#onOutsideInteraction, { signal });
    document.addEventListener('keydown', this.#onEscape, { signal });
  }

  /**
   * Default de restoreFocus: foco dentro, checado antes de esconder
   * (hidden jogaria o foco no body).
   *
   * @param {{ restoreFocus?: boolean }} [options]
   */
  #close(options) {
    const restoreFocus = options?.restoreFocus ?? this.contains(document.activeElement);
    this.#clearTimers();
    this.#whileOpen?.abort();
    this.#whileOpen = null;

    this.dataset.open = 'false';

    const trigger = this.#trigger;
    trigger?.setAttribute('aria-expanded', 'false');

    for (const parent of this.querySelectorAll('[data-departments-parent]')) {
      parent.setAttribute('aria-expanded', 'false');
    }

    if (restoreFocus) trigger?.focus();
  }
}

if (!customElements.get('shopinfo-departments')) {
  customElements.define('shopinfo-departments', ShopinfoDepartmentsComponent);
}
